import type { FC } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import { prefersReducedMotion, registerGsap } from '@/lib/anim'

let lenis: Lenis | null = null

/** Lock/unlock page scrolling from anywhere (the menu overlay uses this). */
export const setScrollLocked = (locked: boolean): void => {
    if (typeof document === 'undefined') return

    if (locked) {
        lenis?.stop()
        document.documentElement.classList.add('lenis-stopped')
        document.body.style.overflow = 'hidden'

        return
    }

    lenis?.start()
    document.documentElement.classList.remove('lenis-stopped')
    document.body.style.overflow = ''
}

/** Smooth-scroll to an element or the top of the page. */
export const scrollToTarget = (target: string | number): void => {
    if (lenis) {
        lenis.scrollTo(target, { offset: -40, duration: 1.4 })

        return
    }

    if (typeof window === 'undefined') return

    if (typeof target === 'number') {
        window.scrollTo({ top: target, behavior: 'smooth' })

        return
    }

    document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' })
}

export const SmoothScroll: FC = () => {
    const router = useRouter()

    useEffect(() => {
        if (prefersReducedMotion()) return

        registerGsap()

        const instance = new Lenis({
            duration: 1.15,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
            touchMultiplier: 1.6,
            autoRaf: false
        })

        lenis = instance

        instance.on('scroll', ScrollTrigger.update)

        const tick = (time: number) => instance.raf(time * 1000)

        gsap.ticker.add(tick)
        gsap.ticker.lagSmoothing(0)

        return () => {
            gsap.ticker.remove(tick)
            instance.destroy()
            lenis = null
        }
    }, [])

    useEffect(() => {
        const onDone = () => {
            lenis?.scrollTo(0, { immediate: true })
            window.scrollTo(0, 0)
            ScrollTrigger.refresh()
        }

        router.events.on('routeChangeComplete', onDone)

        return () => router.events.off('routeChangeComplete', onDone)
    }, [router.events])

    return null
}
